import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from 'axios';
import { PURGE } from 'redux-persist';
import { objectToFormData, saltmineFormDataHeaders, saltmineJwtHeaders, urls } from 'src/libs/reqConf';
import { errorHandle, mergeMessages, sortByCreatedAt } from 'src/utils/function';
import { getOpen, setError } from './modal';

// ----------------------------------------------------------------------
// 네트워크 기관
const initialState = {
  isLoading: false,
  loadingStatus: {}, // 컴포넌트별 로딩 상태 관리
  uploadFileName: '',
  messageList: {},
  isUnread: false,
  currentChannel: { appId: '', channelUrl: '', manager: {} },
  pickMessage: {}
};

const slice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    messageReset(state) {
      // immer를 사용하여 직접 상태를 수정
      Object.assign(state, initialState);
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    closeLoading(state) {
      state.isLoading = false;
    },

    setUploadFileName(state, action) {
      state.uploadFileName = action.payload;
    },

    getMessageListSuccess(state, action) {
      state.isLoading = false;
      const { channelId, docs: newDocs, total, page, pages, limit } = action.payload;

      // 채널 ID별로 메시지 상태 초기화
      if (!state.messageList[channelId]) {
        state.messageList[channelId] = {
          docs: [],
          total: 0,
          page: 1,
          pages: 1,
          limit: 10
        };
      }

      // 기존 메시지와 새 메시지 병합 및 중복 제거
      const mergedMessages = mergeMessages(state.messageList[channelId].docs, newDocs);

      // 정렬된 메시지와 메타데이터를 업데이트
      state.messageList[channelId] = {
        docs: sortByCreatedAt(mergedMessages),
        total,
        page,
        pages,
        limit
      };
    },

    // GET 매니저 정보
    getManagerSuccess(state, action) {
      state.managerInfo = action.payload;
    },

    // UPDATE CURRENT CHANNEL
    updateCurrentChannel(state, action) {
      state.currentChannel = {
        appId: action.payload?.appId,
        channelUrl: action.payload?.channelUrl,
        manager: action.payload?.manager
      };
    },
    // 메시지 추가 및 읽지 않은 메시지 상태 업데이트
    updateMessageUnread(state, action) {
      const { channel, ...message } = action.payload;

      if (!channel?._id) {
        console.error('Channel ID is missing in payload:', action.payload);
        return;
      }

      // 채널 ID를 기준으로 메시지 저장
      if (!state.messageList[channel._id]) {
        // 채널이 처음 생성될 때 기본 구조 설정
        state.messageList[channel._id] = {
          docs: [],
          total: 0,
          page: 1,
          pages: 1,
          limit: 10
        };
      }
      // 메시지 추가
      state.messageList[channel._id].docs = [...state.messageList[channel._id].docs, message];

      // 읽지 않은 메시지 여부 업데이트
      state.isUnread = message.unreadCount >= 1;
    },
    // 특정 채널의 메시지 불러오기
    loadChannelMessages(state, action) {
      const { channelId, messages, total, page, pages, limit } = action.payload;

      state.messageList[channelId] = {
        docs: messages,
        total,
        page,
        pages,
        limit
      };
    },
    extraReducers: (builder) => {
      builder.addCase(PURGE, () => initialState);
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  selectEvent,
  startLoading,
  closeLoading,
  setUploadFileName,
  getMessageListSuccess,
  updateCurrentChannel,
  messageReset,
  changeManagerDesignStatusByManager,
  changeMessageListByManager,
  updateMessageUnread
} = slice.actions;

// ----------------------------------------------------------------------

export function handleForm(status) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.handleDialog(status));
    } catch (error) {
      dispatch(getOpen(error));
    }
  };
}

// ---------------------------------------------------------------------
export function getMessageList(params = {}) {
  const { channelId, page = 1, limit = 30 } = params;

  return async (dispatch) => {
    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (!channelId) {
        console.error('Channel ID is required');
        return;
      }
      const url = `${urls.url}/service/chat/channel/${channelId}/message/paginate?page=${page}&limit=${limit}&autoMarkRead=true`;
      const config = { headers: saltmineJwtHeaders(accessToken) };
      // API 호출
      const response = await axios.get(url, config);

      console.log('Fetched Messages:', response.data.data);

      // Redux 상태 업데이트
      dispatch(
        getMessageListSuccess({
          channelId,
          ...response.data.data
        })
      );
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      dispatch(setError(errorHandle(error)));
      throw error; // 필요시 에러를 호출한 곳으로 전달
    }
  };
}

//---------------------------------------------------------------------
// postSendMessage를 Thunk 액션으로 변경
export const postSendMessage = (params = {}) => {
  return async (dispatch) => {
    const { channelId, ...content } = params;

    dispatch(setUploadFileName(params?.attachments?.name));
    try {
      dispatch(startLoading());
      const accessToken = window.localStorage.getItem('accessToken');
      const url = `${urls.url}/service/chat/channel/${channelId}/message/send`;
      const body = objectToFormData(content);
      const config = { headers: saltmineFormDataHeaders(accessToken) };
      const response = await axios.post(url, body, config);

      return response.data.status;
    } catch (error) {
      console.log('error: ', error);
      dispatch(setError(errorHandle(error))); // 에러 처리 액션 디스패치
    } finally {
      dispatch(closeLoading());
    }
  };
};
